import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, FormControlLabel, Switch, Box, Typography, Stack } from "@mui/material";
import { USERS_LIST_COLUMNS } from "./table-config";
import { useStyles } from "./styles";
import Searchbox from "../../../components/searchbox";
import { UsersListTable } from "./table";
import { getUsers, archiveUser, unarchiveUser, deleteUser } from "../../../apis/users.api";
import { AppAlertBox } from "../../../components/modals/alert-box";
import { showNotification } from "../../../services/utils";

function UsersList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { selectedPage, selectedPageSize, searchValue, deletedOnly } = useSelector(
    (state: any) => state.users.list.filters
  );
  const userList = useSelector((state: any) => state.users.list);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const listParams = {
    selectedPage,
    selectedPageSize,
    searchValue,
    deletedOnly,
  };

  useEffect(() => {
    dispatch(getUsers(listParams));
  }, []);

  const handlePageFilters = ({ selectedPage, selectedPageSize }) => {
    dispatch(
      getUsers({
        selectedPage,
        selectedPageSize,
        searchValue,
        deletedOnly,
      })
    );
  };

  const handleSearch = (value: string) => {
    dispatch(
      getUsers({
        selectedPage: 1,
        selectedPageSize,
        searchValue: value,
        deletedOnly,
      })
    );
  };

  const handleSearchClear = () => {
    dispatch(
      getUsers({
        selectedPage: 1,
        selectedPageSize,
        searchValue: "",
        deletedOnly,
      })
    );
  };

  const handleArchivedOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      getUsers({
        selectedPage: 1,
        selectedPageSize,
        searchValue,
        deletedOnly: event.target.checked,
      })
    );
  };

  const handleArchiveClick = (id: string) => {
    setSelectedUserId(id);
    setShowArchiveConfirm(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedUserId(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUserId) return;

    try {
      const res: any = await dispatch(deleteUser(selectedUserId, true));
      if (res.success) {
        showNotification({
          message: "User deleted successfully",
          type: "success",
        });
        setShowDeleteConfirm(false);
        setSelectedUserId(null);
        dispatch(getUsers(listParams));
      } else {
        showNotification({
          message: res.message || "Failed to delete user",
          type: "error",
        });
      }
    } catch (err) {
      showNotification({
        message: "Failed to delete user",
        type: "error",
      });
    }
  };

  const handleArchiveConfirm = async () => {
    if (!selectedUserId) return;

    try {
      const res: any = await dispatch(archiveUser(selectedUserId));
      if (res.success) {
        showNotification({
          message: "User archived successfully",
          type: "success",
        });
        setShowArchiveConfirm(false);
        setSelectedUserId(null);
        dispatch(getUsers(listParams));
      } else {
        showNotification({
          message: res.message || "Failed to archive user",
          type: "error",
        });
      }
    } catch (err) {
      showNotification({
        message: "Failed to archive user",
        type: "error",
      });
    }
  };

  const handleUnarchiveClick = (id: string) => {
    setSelectedUserId(id);
    setShowUnarchiveConfirm(true);
  };

  const handleUnarchiveConfirm = async () => {
    if (!selectedUserId) return;

    try {
      const res: any = await dispatch(unarchiveUser(selectedUserId));
      if (res.success) {
        showNotification({
          message: "User unarchived successfully",
          type: "success",
        });
        setShowUnarchiveConfirm(false);
        setSelectedUserId(null);
        dispatch(getUsers(listParams));
      } else {
        showNotification({
          message: res.message || "Failed to unarchive user",
          type: "error",
        });
      }
    } catch (err) {
      showNotification({
        message: "Failed to unarchive user",
        type: "error",
      });
    }
  };

  const handleArchiveCancel = () => {
    setShowArchiveConfirm(false);
    setSelectedUserId(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setSelectedUserId(null);
  };

  const handleUnarchiveCancel = () => {
    setShowUnarchiveConfirm(false);
    setSelectedUserId(null);
  };

  const listView: "active" | "archived" = deletedOnly ? "archived" : "active";

  return (
    <>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center" className={classes.searchContainer}>
            <Grid item xs={12} sm={6} md={4}>
              <Searchbox
                onEnter={handleSearch}
                value={searchValue}
                onClear={handleSearchClear}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="flex-end"
                flexWrap="wrap"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={deletedOnly}
                      onChange={handleArchivedOnlyChange}
                      color="primary"
                    />
                  }
                  label="Archived Users"
                />
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <UsersListTable
            list={userList}
            columns={USERS_LIST_COLUMNS}
            handlePageFilters={handlePageFilters}
            appliedFilters={{
              selectedPageSize,
              searchValue,
              selectedPage,
            }}
            onArchiveClick={handleArchiveClick}
            onUnarchiveClick={handleUnarchiveClick}
            onDeleteClick={handleDeleteClick}
            listView={listView}
          />
        </Grid>
      </Grid>

      <AppAlertBox
        isModalOpen={showDeleteConfirm}
        handleClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Are you sure you want to delete this user?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              <strong>Delete:</strong> This will permanently delete the user from the system. This
              action cannot be undone.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />

      <AppAlertBox
        isModalOpen={showArchiveConfirm}
        handleClose={handleArchiveCancel}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Are you sure you want to archive this user?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              <strong>Archive:</strong> This will archive the user. The user will be marked as
              archived and can be viewed using the &quot;Archived Users&quot; toggle. Archived users
              can be restored if needed.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />

      <AppAlertBox
        isModalOpen={showUnarchiveConfirm}
        handleClose={handleUnarchiveCancel}
        onConfirm={handleUnarchiveConfirm}
        onCancel={handleUnarchiveCancel}
        message={
          <Box>
            <Typography fontWeight={600} mb={1.5}>
              Are you sure you want to unarchive this user?
            </Typography>
            <Typography fontSize="14px" color="#6B7280" lineHeight={1.6}>
              <strong>Unarchive:</strong> This will restore the user and they will no longer appear
              in archived users.
            </Typography>
          </Box>
        }
        maxModalWidth="sm"
      />
    </>
  );
}

export default UsersList;
